import React, {useCallback, useEffect, useRef, useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import LaunchSettings from './LaunchSettings';
import PresentationDeck from './PresentationDeck';
import WelcomeStage from './WelcomeStage';

const {FiPause, FiPlay, FiSettings, FiX, FiFastForward} = FiIcons;
const DEFAULT_TRACK = 'https://media.publit.io/file/EventsVideos/Jorge-Luis-Chac-n-Contento-Video-Oficial-128-kbps.mp3';
const TRANSITION_CHIME_URL = ''; // placeholder — leave empty until a file is provided; if empty, skip playing a chime

function LaunchExperience() {
  const [duration, setDuration] = useState(600);
  const [remaining, setRemaining] = useState(600);
  const [endAt, setEndAt] = useState(null);
  const [name, setName] = useState('Your Name');
  const [flyer, setFlyer] = useState('');
  const [language, setLanguage] = useState('en');
  const [musicEnabled, setMusicEnabled] = useState(true);
  const [volume, setVolume] = useState(0.35);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [deckOpen, setDeckOpen] = useState(false);
  const audioRef = useRef(null);
  const deckFadeIntervalRef = useRef(null);
  const endFadeIntervalRef = useRef(null);
  const volumeRef = useRef(volume);

  const running = Boolean(endAt);

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  const clearEndFade = useCallback(() => {
    if (endFadeIntervalRef.current) {
      clearInterval(endFadeIntervalRef.current);
      endFadeIntervalRef.current = null;
    }
  }, []);

  const clearDeckFade = useCallback(() => {
    if (deckFadeIntervalRef.current) {
      clearInterval(deckFadeIntervalRef.current);
      deckFadeIntervalRef.current = null;
    }
  }, []);

  const fadeOutMusic = useCallback((durationMs, intervalRef, onDone) => {
    const el = audioRef.current;
    if (!el || el.paused) {
      onDone?.();
      return;
    }
    if (intervalRef.current) clearInterval(intervalRef.current);
    const startVol = el.volume;
    const stepMs = 100;
    const steps = Math.max(1, Math.round(durationMs / stepMs));
    let step = 0;
    intervalRef.current = setInterval(() => {
      step += 1;
      el.volume = Math.max(0, startVol * (1 - step / steps));
      if (step >= steps) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        el.pause();
        el.volume = volumeRef.current;
        onDone?.();
      }
    }, stepMs);
  }, []);

  useEffect(() => {
    if (!endAt) return undefined;

    const update = () => {
      const next = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
      setRemaining(next);
      if (next === 0) {
        setEndAt(null);
        setRevealed(true);
        // 5s fade-out when countdown completes (not an abrupt cut)
        fadeOutMusic(5000, endFadeIntervalRef);
      }
    };

    update();
    const interval = window.setInterval(update, 250);
    return () => window.clearInterval(interval);
  }, [endAt, fadeOutMusic]);

  useEffect(() => {
    if (!audioRef.current) return;
    if (deckFadeIntervalRef.current || endFadeIntervalRef.current) return;
    audioRef.current.volume = volume;
    audioRef.current.muted = !musicEnabled;
  }, [musicEnabled, volume]);

  useEffect(() => {
    return () => {
      clearEndFade();
      clearDeckFade();
    };
  }, [clearEndFade, clearDeckFade]);

  const start = () => {
    clearEndFade();
    if (remaining <= 0 || revealed) {
      setRemaining(duration);
      setRevealed(false);
      setEndAt(Date.now() + duration * 1000);
    } else {
      setEndAt(Date.now() + remaining * 1000);
    }
    if (musicEnabled) {
      if (audioRef.current) audioRef.current.volume = volumeRef.current;
      audioRef.current?.play().catch(() => {});
    }
    setSettingsOpen(false);
  };

  const pause = () => {
    setEndAt(null);
    clearEndFade();
    audioRef.current?.pause();
  };

  const reset = () => {
    setEndAt(null);
    setRemaining(duration);
    setRevealed(false);
    setDeckOpen(false);
    clearEndFade();
    clearDeckFade();
    audioRef.current?.pause();
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.volume = volumeRef.current;
    }
  };

  const updateDuration = value => {
    const next = Math.max(5, value);
    setDuration(next);
    if (!running) setRemaining(next);
  };

  const openDeck = useCallback(() => {
    // If the 5s countdown-end fade is still running, abort it — deck-open should feel snappy
    clearEndFade();

    const finish = () => {
      if (TRANSITION_CHIME_URL) {
        try {
          const chime = new Audio(TRANSITION_CHIME_URL);
          chime.play().catch(() => {});
        } catch {
          /* skip */
        }
      }
      setDeckOpen(true);
    };

    const el = audioRef.current;
    if (musicEnabled && el && !el.paused) {
      // Short ~1.2s fade then chime (step every 50ms to match prior behavior)
      if (deckFadeIntervalRef.current) clearInterval(deckFadeIntervalRef.current);
      const startVol = el.volume;
      const steps = Math.max(1, Math.round(1200 / 50));
      let step = 0;
      deckFadeIntervalRef.current = setInterval(() => {
        step += 1;
        el.volume = Math.max(0, startVol * (1 - step / steps));
        if (step >= steps) {
          clearInterval(deckFadeIntervalRef.current);
          deckFadeIntervalRef.current = null;
          el.pause();
          el.volume = volumeRef.current;
          finish();
        }
      }, 50);
    } else {
      finish();
    }
  }, [musicEnabled, clearEndFade]);

  const jumpToPresentation = () => {
    setEndAt(null);
    setRevealed(true);
    openDeck();
  };

  return (
    <main className="experience">
      <audio ref={audioRef} src={DEFAULT_TRACK} loop preload="none" />

      <WelcomeStage
        remaining={remaining}
        duration={duration}
        name={name}
        flyer={flyer}
        language={language}
        revealed={revealed}
        onOpenDeck={openDeck}
      />

      <div className="launch-toolbar">
        <span className={`status-dot ${running ? 'active' : ''}`} />
        <span className="launch-status">{running ? 'LIVE' : revealed ? 'WELCOME' : 'READY'}</span>
        <button onClick={running ? pause : start} title={running ? 'Pause' : 'Start'}>
          <SafeIcon icon={running ? FiPause : FiPlay} />
        </button>
        <button
          onClick={jumpToPresentation}
          title={language === 'es' ? 'Ir a la presentación' : 'Jump to presentation'}
        >
          <SafeIcon icon={FiFastForward} />
        </button>
        <button onClick={() => setSettingsOpen(value => !value)} title="Settings">
          <SafeIcon icon={settingsOpen ? FiX : FiSettings} />
        </button>
      </div>

      <LaunchSettings
        open={settingsOpen}
        duration={duration}
        onDuration={updateDuration}
        name={name}
        onName={setName}
        flyer={flyer}
        onFlyer={setFlyer}
        language={language}
        onLanguage={setLanguage}
        musicEnabled={musicEnabled}
        onMusicEnabled={setMusicEnabled}
        volume={volume}
        onVolume={setVolume}
        running={running}
        onStart={start}
        onPause={pause}
        onReset={reset}
        onJumpToPresentation={jumpToPresentation}
      />

      {deckOpen && (
        <PresentationDeck
          language={language}
          name={name}
          onClose={() => setDeckOpen(false)}
        />
      )}
    </main>
  );
}

export default LaunchExperience;
